import {useContext, useState} from 'react'

import Context from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'
import {useRouter} from '@s-ui/react-router'
import {formPublishAdPhotoUploaderActions} from '@adv-mt/mt-form-publish-ad-photo-uploader'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import {STEPS} from '../../index.js'

const useUploadImages = ({adId, destinationUrl: _destinationUrl, fields, pageName}) => {
  const {domain} = useContext(Context)
  const {page: pageTrack, track} = useAnalyticsTrack()
  const router = useRouter()

  const [isLoading, setIsLoading] = useState(false)
  const [numberOfImagesUploaded, setNumberOfImagesUploaded] = useState(0)
  const [showNotification, setShowNotification] = useState(true)
  const [imageUrls, setImageUrls] = useState([])
  const [isGeneratingComment, setIsGeneratingComment] = useState(false)

  const config = domain.get('config')
  const {IS_AI_GENERATED_COMMENTS_FEATURE_ENABLED} = config.get('seller')
  const URL_STATICS_IMAGES = config.get('URL_STATICS_IMAGES')
  const {RECOMMENDED_NUMBER_OF_UPLOADED_IMAGES} = config.get('seller')
  const {formFieldIds} = config.get('form')

  useMount(async () => {
    const trackingData = await domain.get('get_page_tracking_data_use_case').execute({
      page: pageName,
      step: STEPS.uploadImages
    })

    pageTrack(trackingData)
  })

  const trackOnImagesInserted = () => {
    track({
      event: 'Ad Insertion Picture Loaded'
    })
  }

  const trackOnUploadImageErrors = ({reason}) => {
    track({
      event: 'Ad Insertion Picture Error',
      data: {page_error: reason}
    })
  }

  const trackOnClickPhotoUploader = () => {
    track({
      event: 'Ad Insertion Add Picture Clicked'
    })
  }

  const trackOnImagesSelected = () => {
    setIsLoading(true)
    track({
      event: 'Ad Insertion Picture Selected'
    })
  }

  const handleSubmit = async () => {
    track({
      event: 'Ad Insertion Button Clicked',
      data: {
        hit_information: numberOfImagesUploaded ? 'seguir con fotos' : 'seguir sin fotos',
        hit_origin: 'pta insertar fotos'
      }
    })

    let destinationUrl = _destinationUrl

    if (IS_AI_GENERATED_COMMENTS_FEATURE_ENABLED) {
      setIsLoading(true)
      setIsGeneratingComment(true)

      const [error, response] = await domain.get('generate_comment_ad_publisher_use_case').execute({adId})

      if (error) {
        track({
          event: `Ad Insertion AI Description Error`,
          data: {page_error: `Network Error`}
        })
        destinationUrl = `${destinationUrl}&aiGeneratedStatus=error`
      } else {
        track({
          event: `Ad Insertion AI Description Success`
        })
        const {colour, comments} = response || {}

        await domain.get('edit_draft_ad_publisher_use_case').execute({
          adId,
          fields: {...fields, colour, comments},
          images: imageUrls
        })

        destinationUrl = `${destinationUrl}&aiGeneratedStatus=success`
      }
    }

    router.push(destinationUrl)
  }

  const handleUploadPhotosRejected = (rejectedPhotos = []) => {
    setIsLoading(false)

    if (rejectedPhotos[0]) {
      trackOnUploadImageErrors({reason: rejectedPhotos[0].reason})
    }
  }

  const handleUploadPhotosSuccess = async (images, info = {}) => {
    if (info.action === formPublishAdPhotoUploaderActions.UPLOAD) {
      trackOnImagesInserted()
    }

    const _imageUrls = images.map(photo => photo.url)

    const numberOfImagesUploaded = _imageUrls.length

    setShowNotification(numberOfImagesUploaded < RECOMMENDED_NUMBER_OF_UPLOADED_IMAGES)
    setNumberOfImagesUploaded(numberOfImagesUploaded)
    setImageUrls(_imageUrls)

    await domain.get('edit_draft_ad_publisher_use_case').execute({
      adId,
      fields,
      images: _imageUrls
    })

    setIsLoading(false)
  }

  return {
    config: {
      IMAGES_FIELD_ID: formFieldIds.images,
      RECOMMENDED_NUMBER_OF_UPLOADED_IMAGES,
      URL_STATICS_IMAGES
    },
    handlers: {
      handleSubmit,
      handleUploadPhotosRejected,
      handleUploadPhotosSuccess
    },
    state: {
      isLoading,
      isGeneratingComment,
      numberOfImagesUploaded,
      showNotification
    },
    tracking: {
      trackOnClickPhotoUploader,
      trackOnImagesSelected
    }
  }
}

export {useUploadImages}
